import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, PenLineIcon, StarIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useReviews } from '../../hooks/useReviews';
import { reviewsAggregate } from '../../data/reviews';
import { testimonials } from '../../data/testimonials';
import type { Testimonial } from '../../data/testimonials';
import { buttonClasses } from '../../utils/buttonStyles';
import { RatingStars } from '../reviews/RatingStars';
import { ReviewForm } from '../reviews/ReviewForm';
import { REVIEWS_SECTION_ID } from '../reviews/ReviewSummaryBar';

/** Customer Reviews header (rating + write a review) above two endless rows of testimonial cards. */
export function TestimonialsMarquee() {
  const {
    lang
  } = useLanguage();
  const es = lang === 'es';
  const {
    summary,
    hasReviewed,
    submit
  } = useReviews();
  const [writing, setWriting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const fmt = (n: number) => n.toLocaleString(es ? 'es-US' : 'en-US');
  const half = Math.ceil(testimonials.length / 2);
  const rows = [testimonials.slice(0, half), testimonials.slice(half)];
  return <section id={REVIEWS_SECTION_ID} tabIndex={-1} aria-labelledby="reviews-title" className="mf-tech-grid scroll-mt-[72px] overflow-hidden border-t mf-hairline bg-deep py-24 focus:outline-none md:py-32" data-id="element-381" data-mp="21j6r3bvy9dwd">
      <div className="mx-auto max-w-site px-5 md:px-8" data-id="element-382" data-mp="1wklnd2hhoc36">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between" data-id="element-383" data-mp="1wm6hiqzw5t0i">
          <div data-id="element-384" data-mp="1iui48kdwoyhy">
            <h2 id="reviews-title" className="font-display text-5xl font-semibold uppercase leading-[0.92] md:text-7xl" data-id="element-385" data-mp="1iv1pob2rm50q">
              {es ? 'Reseñas de clientes' : 'Customer Reviews'}
            </h2>
            <div className="mt-8 flex items-end gap-4" data-id="element-386" data-mp="18vswhkfq4uff">
              <span className="font-display text-7xl font-semibold leading-none text-white" data-id="element-387" data-mp="1dudpaysncbtu">
                {summary.count ? summary.average.toFixed(1) : '—'}
              </span>
              <div className="pb-1" data-id="element-388" data-mp="pz27ze6zzfwh">
                <RatingStars value={summary.average} size={22} decorative data-id="element-389" data-mp="py0lmdkj76zg" />
                <p className="mt-1 text-sm text-titanium" data-id="element-390" data-mp="kywd7p5flda6">
                  {summary.count ? summary.isAggregate && reviewsAggregate ? `${fmt(summary.count)}+ ${es ? 'reseñas' : 'reviews'} · ${es ? 'desde' : 'since'} ${reviewsAggregate.sinceYear}` : `${fmt(summary.count)} ${es ? 'reseñas verificadas' : 'verified reviews'}` : es ? 'Aún no hay reseñas publicadas' : 'No published reviews yet'}
                </p>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-[320px]" data-id="element-391" data-mp="3ogxt8ll1xubf">
            {hasReviewed ? <div className="flex items-start gap-3 rounded-xl border border-marine/40 bg-marine/[0.08] p-4" role="status" data-id="element-392" data-mp="3jhbged94yg35">
                <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-marine" aria-hidden data-id="element-393" data-mp="1z028oq8kjejp" />
                <p className="font-semibold text-white" data-id="element-394" data-mp="23xlx2iyzd7pd">
                  {justSubmitted ? es ? '¡Gracias! Recibimos tu reseña.' : 'Thank you! We received your review.' : es ? 'Ya dejaste tu reseña. ¡Gracias!' : 'You already left a review. Thank you!'}
                </p>
              </div> : !writing && <button type="button" onClick={() => setWriting(true)} className={`${buttonClasses('primary')} w-full`} data-id="element-395" data-mp="2rnyceewk26pz">
                  <PenLineIcon className="h-4 w-4" aria-hidden data-id="element-396" data-mp="2wnkp8n8h1ky9" /> {es ? 'Escribir una reseña' : 'Write a review'}
                </button>}
          </div>
        </div>

        <AnimatePresence initial={false} data-id="element-397" data-mp="128i411qggp15">
          {writing && !hasReviewed && <motion.div initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="mt-10 max-w-2xl" data-id="element-398" data-mp="2sq57agkfevvt">
              <ReviewForm onCancel={() => setWriting(false)} onSubmit={r => {
            submit(r);
            setWriting(false);
            setJustSubmitted(true);
          }} data-id="element-399" data-mp="31opjcyvhe7b2" />
            </motion.div>}
        </AnimatePresence>

        <p className="mt-14 text-sm font-semibold uppercase tracking-[0.14em] text-titanium/80" data-id="element-400" data-mp="2irxso4dm0pwz">
          {es ? 'Primeras impresiones del diseño del M1 PRO' : 'First impressions of the M1 PRO design'}
        </p>
      </div>

      <div className="mf-marquee-wrap mt-5 space-y-5" data-id="element-401" data-mp="1qb528uzoshd6">
        {rows.map((row, r) => <div key={r} className="mf-marquee-mask" data-id="element-402" data-mp="1mal35lvjz3oe" data-mpi={r}>
            <ul className={`mf-marquee flex w-max gap-5 ${r === 1 ? 'mf-marquee-rev' : ''}`} data-id="element-403" data-mp="1m9t7yc5vb7yn">
              {[...row, ...row].map((t, i) => <TestimonialCard key={`${t.name}-${i}`} t={t} hidden={i >= row.length} data-id="element-404" data-mp="1gxcy9sz1xfzv" data-mpi={i} />)}
            </ul>
          </div>)}
      </div>
    </section>;
}
function TestimonialCard({
  t,
  hidden
}: {
  t: Testimonial;
  hidden: boolean;
}) {
  return <li aria-hidden={hidden || undefined} className="flex w-[300px] shrink-0 flex-col border border-marine/20 bg-[#06121F]/90 p-6 sm:w-[360px]" data-id="element-405" data-mp="174xcslojq9a8">
      <div className="flex gap-0.5" aria-label={`${t.rating} out of 5 stars`} role="img" data-id="element-406" data-mp="1zue6dmnwjw81">
        {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className={`h-4 w-4 ${i < t.rating ? 'fill-[#FFC94D] text-[#FFC94D]' : 'text-white/20'}`} aria-hidden data-id="element-407" data-mp="1l0j08of4sl0j" />)}
      </div>
      <p className="mt-4 font-display text-xl font-semibold uppercase leading-tight tracking-[0.03em] text-white" data-id="element-408" data-mp="9ldz3ouneuj1">{t.title}</p>
      <p className="mt-2 text-[15px] leading-relaxed text-titanium" data-id="element-409" data-mp="2h4ruqfm33frx">{t.body}</p>
      <p className="mt-auto pt-5 text-sm font-semibold text-ice" data-id="element-410" data-mp="2h6ufol8dqips">{t.name}</p>
    </li>;
}